import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { WidgetParent } from "../../class/widget-parent";
import { ComponentAdapterFactory } from "../../class/adapters/component-adapter-factory";
import { SmartPaletteGeneral } from "../../class/smart-palette";
import { ComponentAdapterSensor } from "../../class/adapters/component-adapter-sensor";
import { StaticClass } from "../../class/general-classes/static-classes";
import { IconHelper } from "../../class/icon-helper";
import { IonButton, IonIcon, IonToggle } from "@ionic/angular";
import { DraggableSliderComponent } from "../draggable-slider/draggable-slider.component";
import { SmartComponentSpinnerComponent } from "../smart-component-spinner/smart-component-spinner.component";
import { ErrorLoadingWidgetComponent } from "../error-loading-widget/error-loading-widget.component";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
    selector: "app-widget-sensor",
    templateUrl: "./widget-sensor.component.html",
    styleUrls: ["./widget-sensor.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonButton, IonIcon, IonToggle, DraggableSliderComponent, SmartComponentSpinnerComponent, ErrorLoadingWidgetComponent, TranslatePipe]
})

export class WidgetSensorComponent extends WidgetParent<ComponentAdapterSensor> {
  public isExpanded: boolean = false;
  public areIconsBigger: boolean = false;

  // Sensor variables
  public ambientBrightness: string = "0";
  public brightnessSetpointValue: number = 0;
  public isAutomaticModeBrightness: boolean = false;
  public isAnyonePresent: boolean = false;

  // Dimmer variables
  private _iconDimmerOn = "assets/img/dimmer/ico-dimmer-on-no-bg.svg";
  private _iconDimmerOff = "assets/img/dimmer/ico-dimmer-off-no-bg.svg";
  public iconDimmerState: string = "";
  public faultState: boolean = false;
  public dimmerBrightnessValue: number = 0;

  //Color temperature variables
  public currentColorTemperature: string = "0";
  public colorTemperatureSetpoint: number = 0;
  public isAutomaticModeColorTemperature: boolean = false;

  //Color of temperature automan icon that changes according to value
  public colorTempAutoIcon: string = "";

  // Colors for color temperature slider
  public gradientColors: Array<string> = [];

  public iconHelper = IconHelper;

  constructor() {
    super();

    this._iconDimmerOn = IconHelper.getIconsForDimmer().on;
    this._iconDimmerOff = IconHelper.getIconsForDimmer().off;

    this.iconDimmerState = this._iconDimmerOn;
  }

  /**
   * Handle click for opening and closing widget
   */
  public onClickOpenCloseWidget(): void {
    this.isExpanded = !this.isExpanded;
  }

  /**
   * Handle brightness slider callback
   * @param value value received from slider
   */
  public onSlideBrightnessSetpoint(value: number): void {
    if (this.componentAdapter.AmbientBrightnessSetpoint) {
      if (value !== this.brightnessSetpointValue) {
        this.sendCommand(value, "AmbientBrightnessSetpoint");
      }
    }
  }

  /**
   * Handle color temperature slider callback
   * @param value value received from slider
   */
  public onSlideColorTemperatureSetpoint(value: number): void {

    if (this.componentAdapter.SetpointColorTemperature) {
      if (value !== this.colorTemperatureSetpoint) {
        this.sendCommand(value, "SetpointColorTemperature");
      }
    }
  }

  /**
   * Callback event for switch automatic mode for dimmer brightnesss
   * @param event Event received from the switch
   */
  public onSwitchAutoManBrightness(event: Event): void {
    //checked: true is automatic mode
    this.isAutomaticModeBrightness = (event as CustomEvent).detail.checked;
    const valueToSend = this.isAutomaticModeBrightness ? 1 : 0;

    this.sendCommand(valueToSend, "CommandAutomanBrightness");
  }

  /**
   * Callback event for switch automatic mode for color temperature
   * @param event Event received from the switch
   */
  public onSwitchAutoManColorTemperature(event: Event): void {
    //checked: true is automatic mode
    this.isAutomaticModeColorTemperature = (event as CustomEvent).detail.checked;
    const valueToSend = this.isAutomaticModeColorTemperature ? 1 : 0;

    this.sendCommand(valueToSend, "ColorTemperatureAutoman");
  }

  /**
   * When the dimmer button is clicked turn on/off dimmer light
   */
  public onClickSwitchDimmer(): void {
    this.switchDimmer();
  }

  /**
   * Add class "turn-on" to dimmer button if it's turned on
   * @returns boolean
   */
  public toggleClassForButton(): boolean {
    return this.iconDimmerState === this._iconDimmerOn ? true : false;
  }

  /**
   * Callback for dimmer brightness slider value change
   *
   * @param value Value to set
   */
  public onSlideDimmer(value: number): void {
    if (this.componentAdapter.DimmerCommand) {
      if (value !== this.dimmerBrightnessValue) {
        this.sendCommand(value, "DimmerCommand");
      }
    }
  }

  /**
   * Get the correct color for automan temperature based on value
   * @param value value from slider
   */
  public getColorTemperatureIconColor(value: number): void {
    if (value <= 3000) {
      this.colorTempAutoIcon = "#F95700";
    } else if (value > 3000 && value <= 5000) {
      this.colorTempAutoIcon = "#FF8C09";
    } else if (value > 5000) {
      this.colorTempAutoIcon = "#3EA3FF";
    }
  }

  /**
   * Get the colors that compose the gradient for color temperature slider
   * @returns array with hexadecimal color
   */
  public getGradientColorsForSlider(): Array<string> {
    let colorsArray = []

    if (+this.componentAdapter.SetpointColorTemperatureMin <= 3000 || +this.componentAdapter.SetpointColorTemperatureMax <= 3000) {
      colorsArray.push("#ff5900", "#ffbe09");
    }

    if (+this.componentAdapter.SetpointColorTemperatureMin <= 5000 || +this.componentAdapter.SetpointColorTemperatureMax <= 5000) {
      if (+this.componentAdapter.SetpointColorTemperatureMin > 3000 || +this.componentAdapter.SetpointColorTemperatureMax > 3000) {
        colorsArray.push("#ffd400", "#FFF585");
      }
    }

    if (+this.componentAdapter.SetpointColorTemperatureMin > 5000 || +this.componentAdapter.SetpointColorTemperatureMax > 5000) {
      if (+this.componentAdapter.SetpointColorTemperatureMin <= 5000 || +this.componentAdapter.SetpointColorTemperatureMax <= 5000) {
        colorsArray.push("#ffffff", "#8cc8ff");
      } else {
        colorsArray.push("#D3EBFF", "#8cc8ff");
      }
    }
    return colorsArray;
  }

  /**
   * Check if there are any component used in the expanded part of the component. 
   * If there aren't any hide the arrow to expand the component.
   * @returns boolean value
   */
  public checkIfComponentIsExpandable(): boolean {
    if ((this.componentAdapter.DimmerCommand || this.componentAdapter.DimmerState || this.componentAdapter.DimmerOnOffCommand || this.componentAdapter.CommandAutomanBrightness
      || this.componentAdapter.ColorTemperatureAutoman || this.componentAdapter.SetpointColorTemperature) && !this.isSupervision && !this.isSupervisionModalOpen) {
      return true;
    }
    return false;
  }

  protected setWidgetSmartPalette(): SmartPaletteGeneral {
    let widgetSmaertPalette = new SmartPaletteGeneral();

    return widgetSmaertPalette;
  }

  protected onComponentLoad(): void {
  }

  protected onSmartComponentErrorLoading(): void { }

  protected onComponentDestroy(): void { }

  protected onSmartComponentSuccessLoaded(): void | boolean {
    const factory = new ComponentAdapterFactory();
    this.componentAdapter = factory.createComponentAdapter(
      this._listSmartComponentSkills,
      ComponentAdapterSensor
    );

    this.titleWidget = this.componentAdapter.Name;

    //If component title is empty show error loading component
    if (!this.titleWidget) {
      return this._errorLoading = true;
    }

    this.isExpanded = this._isExpanded;

    this.areIconsBigger = !(this.componentAdapter.AmbientBrightness || this.componentAdapter.AmbientBrightnessSetpoint || this.componentAdapter.CurrentColorTemperature) ? true : false;

    if (this.componentAdapter.SetpointColorTemperature) {
      this.gradientColors = this.getGradientColorsForSlider();
    }
  }

  protected onAddressValueChange(
    instanceGUID: string,
    address: string,
    value: string
  ): void {

    if (this.componentAdapter.AmbientBrightness) {
      this.tryToUpdateAmbientBrightness(instanceGUID, address, value);
    }

    if (this.componentAdapter.AmbientBrightnessSetpoint) {
      this.tryToUpdateBrightnessSetpoint(instanceGUID, address, value);
    }

    if (this.componentAdapter.Presence) {
      this.tryToUpdatePresence(instanceGUID, address, value);
    }

    if (this.componentAdapter.StateAutomanBrightness) {
      this.tryToUpdateAutoManState(instanceGUID, address, value);
    }

    if (this.componentAdapter.DimmerOnOffState) {
      this.tryUpdateIconDimmerState(instanceGUID, address, value);
    }

    if (this.componentAdapter.DimmerState) {
      this.tryUpdateDimmerBrightnessValue(instanceGUID, address, value);
    }

    if (this.componentAdapter.FaultState) {
      this.tryUpdateFaultState(instanceGUID, address, value);
    }

    if (this.componentAdapter.CurrentColorTemperature) {
      this.tryToUpdateCurrentColorTemperature(instanceGUID, address, value);
    }

    if (this.componentAdapter.ColorTemperatureState) {
      this.tryToUpdateColorTemperatureState(instanceGUID, address, value);
    }

    if (this.componentAdapter.SetpointColorTemperature) {
      this.tryToUpdateColorTemperatureSetpoint(instanceGUID, address, value);
    }
  }

  /**
   * Try to test received value and update the color temperature state
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value Value to apply
   */
  private tryToUpdateColorTemperatureState(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.ColorTemperatureState.instanceGUID &&
      address === this.componentAdapter.ColorTemperatureState.address
    ) {
      this.isAutomaticModeColorTemperature = parseInt(value) === 1;
    }
  }

  /**
 * Try to test received value and update the ambient brightness value
 *
 * @param instanceGUID Instance GUID to test
 * @param address address to test
 * @param value Value to apply
 */
  private tryToUpdateCurrentColorTemperature(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.CurrentColorTemperature.instanceGUID &&
      address === this.componentAdapter.CurrentColorTemperature.address
    ) {
      this.currentColorTemperature = StaticClass.formatDecimalValue(value, 1, true);
    }
  }

  /**
  * Try to test received value and update the ambient brightness value
  *
  * @param instanceGUID Instance GUID to test
  * @param address address to test
  * @param value Value to apply
  */
  private tryToUpdateColorTemperatureSetpoint(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.SetpointColorTemperature.instanceGUID &&
      address === this.componentAdapter.SetpointColorTemperature.address
    ) {
      this.colorTemperatureSetpoint = parseInt(StaticClass.formatDecimalValue(value, 0));
      this.getColorTemperatureIconColor(this.colorTemperatureSetpoint);
    }
  }


  /**
   * Try to test received value and update the ambient brightness value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value Value to apply
   */
  private tryToUpdateAmbientBrightness(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.AmbientBrightness.instanceGUID &&
      address === this.componentAdapter.AmbientBrightness.address
    ) {
      this.ambientBrightness = StaticClass.formatDecimalValue(value, 1, true);
    }
  }

  /**
   * Try to test received value and update the ambient brightness value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value Value to apply
   */
  private tryToUpdateBrightnessSetpoint(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.AmbientBrightnessSetpoint.instanceGUID &&
      address === this.componentAdapter.AmbientBrightnessSetpoint.address
    ) {
      this.brightnessSetpointValue = parseInt(StaticClass.formatDecimalValue(value, 0));
    }
  }

  /**
   * Try to test received value and update the presence value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value Value to apply
   */
  private tryToUpdatePresence(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.Presence.instanceGUID &&
      address === this.componentAdapter.Presence.address
    ) {
      this.isAnyonePresent = parseInt(value) === 1;
    }
  }

  /**
   * Try to test received value and update the presence value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value Value to apply
   */
  private tryToUpdateAutoManState(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.StateAutomanBrightness.instanceGUID &&
      address === this.componentAdapter.StateAutomanBrightness.address
    ) {
      this.isAutomaticModeBrightness = parseInt(value) === 1;
    }
  }

  /**
   * Send command to mqtt for sensor and dimmer
   * @param value Value to set over the selected command address
   */
  private sendCommand(value: number | string, commandName: string): void {
    const instanceGUID: string = this.componentAdapter[commandName]?.instanceGUID;
    const address: string = this.componentAdapter[commandName]?.address;

    this.sendAddressCommand(instanceGUID, address, value.toString());
  }

  /**
   * Try to update the icon dimmer state based on the value of the address
   *
   * @param instanceGUID Instance GUID of the address changed
   * @param address Address received
   * @param value Value received
   */
  private tryUpdateIconDimmerState(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.DimmerOnOffState.instanceGUID &&
      address === this.componentAdapter.DimmerOnOffState.address
    ) {
      if (value === "1") {
        this.iconDimmerState = this._iconDimmerOn;
      } else {
        this.iconDimmerState = this._iconDimmerOff;
      }
    }
  }

  /**
   * Try to update the dimmer brightness value
   *
   * @param instanceGUID Instance GUID of the address changed
   * @param address Address received
   * @param value Value received
   */
  private tryUpdateDimmerBrightnessValue(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.DimmerState.instanceGUID &&
      address === this.componentAdapter.DimmerState.address
    ) {
      this.dimmerBrightnessValue = parseFloat(value);
    }
  }

  /**
   * Try to update fault state of the dimmer
   *
   * @param instanceGUID Instance GUID of the address changed
   * @param address Address received
   * @param value Value received
   */
  private tryUpdateFaultState(
    instanceGUID: string,
    address: string,
    value: string
  ) {
    if (
      instanceGUID === this.componentAdapter.FaultState.instanceGUID &&
      address === this.componentAdapter.FaultState.address
    ) {
      this.faultState = parseInt(value) === 1;
    }
  }

  /**
   * Switch light on/off dimmer
   */
  private switchDimmer(): void {
    if (this.componentAdapter.DimmerOnOffCommand) {
      this.sendCommand("VALUE NOT", "DimmerOnOffCommand");
    }
  }
}
